import { z } from 'zod';

import { makeEndpoint, paginateResponseSchema } from '../common';

export enum WebhookEntityType {
  BRAND = 'brand',
  ORGANIZATION = 'organization',
}

export enum WebhookEvents {
  BOOKING_CANCELLED = 'booking.cancelled',
  EXPERIENCE_PUBLISHED = 'experience.published',
  LISTING_PUBLISHED = 'listing.published',
  LISTING_UNPUBLISHED = 'listing.unpublished',
  BOOKING_COMPLETED = 'booking.completed',
  BOOKING_REFUNDED = 'booking.refunded',
  BOOKING_RESCHEDULED = 'booking.rescheduled',
  FINANCIAL_REPORT_UPDATED = 'financialReport.updated',
  FINANCIAL_REPORT_V2_UPDATED = 'financialReportV2.updated',
  REVIEW_COMPLETED = 'review.completed',
  BOOKING_MODIFIED = 'booking.modified',
}

export const WebhookResponseSchema = z.object({
  id: z.string(),
  url: z.string().url(),
  events: z.array(z.nativeEnum(WebhookEvents)),
  status: z.boolean(),
  userId: z.string().uuid(),
  secret: z.string().nullable().optional(),
  brandId: z.string().uuid().nullable(),
  organizationId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});

export type WebhookResponse = z.infer<typeof WebhookResponseSchema>;

export const PaginatedWebhookResponseSchema = paginateResponseSchema(WebhookResponseSchema);
export type PaginatedWebhookResponse = z.infer<typeof PaginatedWebhookResponseSchema>;

export const WebhookRequestSchema = z.object({
  url: z.string().url(),
  events: z.array(z.nativeEnum(WebhookEvents)).min(1),
  status: z.boolean(),
  secret: z.string().optional(),
});

export const GetPaginatedWebhooksEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ entityType, entityId }: { entityType: string; entityId: string }) => `v1/${entityType}/${entityId}/webhooks`,
  responseSchema: PaginatedWebhookResponseSchema,
  requestSchema: z.void(),
});

export const GetWebhookDetailEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ entityType, entityId, webhookId }: { entityType: string; entityId: string; webhookId: string }) =>
    `v1/${entityType}/${entityId}/webhooks/${webhookId}`,
  responseSchema: WebhookResponseSchema,
  requestSchema: z.void(),
});

export const CreateWebhookEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ entityType, entityId }: { entityType: string; entityId: string }) => `v1/${entityType}/${entityId}/webhooks`,
  responseSchema: z.void(),
  requestSchema: WebhookRequestSchema,
});

export const UpdateWebhookEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ entityType, entityId, webhookId }: { entityType: string; entityId: string; webhookId: string }) =>
    `v1/${entityType}/${entityId}/webhooks/${webhookId}`,
  responseSchema: z.void(),
  requestSchema: WebhookRequestSchema,
});

export const DeleteWebhookEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ entityType, entityId, webhookId }: { entityType: string; entityId: string; webhookId: string }) =>
    `v1/${entityType}/${entityId}/webhooks/${webhookId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});
